import { AssistantToolResult } from '../../interfaces/Assistant';

export type EntityKind = 'client' | 'booking' | 'container' | 'activity' | 'meeting';

export interface EntityRef {
  kind: EntityKind;
  id?: number;
  label: string;
  /** Pentru containere (și uneori bookinguri): bookingul părinte, pentru navigare. */
  bookingId?: number;
  /** Pentru activități: clientul, necesar la deschiderea modalului. */
  clientId?: number;
}

/**
 * Derivă referințe „clickable" către entități din rezultatele tool-urilor unui mesaj.
 * Așa, când AI-ul menționează un client/booking/container/activitate, putem afișa
 * un chip care deschide modalul sau pagina de view corespunzătoare.
 */
export function extractEntityRefs(toolResults?: AssistantToolResult[]): EntityRef[] {
  const refs: EntityRef[] = [];
  const seen = new Set<string>();

  const add = (ref: EntityRef) => {
    if (!ref.label) return;
    const key = `${ref.kind}:${ref.id ?? ref.label}`;
    if (seen.has(key)) return;
    seen.add(key);
    refs.push(ref);
  };

  for (const r of toolResults || []) {
    const o = r.output as any;
    if (!o || typeof o !== 'object') continue;

    // Clienți
    if (Array.isArray(o.clients)) o.clients.forEach((c: any) => c && add({ kind: 'client', id: c.id, label: c.company || `Client #${c.id}` }));
    if ((r.toolName === 'getClient' || r.toolName === 'updateClient') && o.id && o.company) add({ kind: 'client', id: o.id, label: o.company });
    if (r.toolName === 'updateClient' && o.client) add({ kind: 'client', id: o.client.id, label: o.client.company || `Client #${o.client.id}` });

    // Bookinguri (din liste sau detalii)
    if (Array.isArray(o.bookings)) o.bookings.forEach((b: any) => b && add({ kind: 'booking', id: b.id, label: b.number || `Booking #${b.id}`, clientId: b.client?.id ?? o.clientId }));
    if (r.toolName === 'getBooking' && o.id && o.number) add({ kind: 'booking', id: o.id, label: o.number, clientId: o.client?.id });
    if (r.toolName === 'getClient' && Array.isArray(o.recentBookings)) o.recentBookings.forEach((b: any) => b && add({ kind: 'booking', id: b.id, label: b.number || `Booking #${b.id}`, clientId: o.id }));

    // Containerele nu au pagină proprie de view → le linkăm prin bookingul părinte
    // (acolo se află tab-ul de containere). Afișăm un chip de booking per container găsit.
    if (r.toolName === 'getContainer' && Array.isArray(o.containers)) {
      o.containers.forEach((c: any) => {
        if (c && c.bookingId) add({ kind: 'booking', id: c.bookingId, label: c.bookingNumber || `Booking #${c.bookingId}` });
      });
    }

    // Activități
    if ((r.toolName === 'createActivity' || r.toolName === 'updateActivity') && o.id) add({ kind: 'activity', id: o.id, label: `Activitate #${o.id}`, clientId: o.clientId });

    // Ședințe
    if (Array.isArray(o.meetings)) o.meetings.forEach((m: any) => m && add({ kind: 'meeting', id: m.id, label: m.title || `Ședință #${m.id}` }));
    if (r.toolName === 'getMeeting' && o.id && o.title) add({ kind: 'meeting', id: o.id, label: o.title });
    if (r.toolName === 'getMeetingTranscript' && o.meetingId) add({ kind: 'meeting', id: o.meetingId, label: o.title || `Ședință #${o.meetingId}` });
    if (r.toolName === 'compareMeetingToActivities' && o.meeting?.id) add({ kind: 'meeting', id: o.meeting.id, label: o.meeting.title || `Ședință #${o.meeting.id}` });
  }

  return refs.slice(0, 10);
}

/** Deschide entitatea (modal sau navigare) prin event bus — vezi AssistantEntityHost. */
export function openEntity(ref: EntityRef): void {
  window.dispatchEvent(new CustomEvent('assistant:open-entity', { detail: ref }));
}
